"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  ADOPTION_REGIONS,
  type AdoptionRegion,
  julianFromFixed,
} from "@/lib/tool-kit/calendars/julian";
import { gregorianFromRd, weekdayFromRd } from "@/lib/tool-kit/calendars/rd";
import {
  type CalendarKind,
  dayOfYear,
  doomsday,
  isLeapYearIn,
  resolveDate,
  zeller,
} from "@/lib/tool-kit/calendars/weekday";
import type { CivilDate } from "@/lib/tool-kit/converters/week-number";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** English strings. A translated content file overrides these through `ui`. */
export const DAY_OF_WEEK_CALCULATOR_STRINGS = {
  year: "Year",
  month: "Month",
  day: "Day",
  calendar: "Calendar",
  calGregorian: "Gregorian throughout (proleptic)",
  calJulian: "Julian throughout (proleptic)",
  regions: {
    papalSpain: "As used in Spain, Portugal and Italy (Julian to 4 Oct 1582)",
    france: "As used in France (Julian to 9 Dec 1582)",
    denmark:
      "As used in Denmark–Norway and Protestant Germany (to 18 Feb 1700)",
    britain: "As used in Great Britain and colonies (Julian to 2 Sep 1752)",
    sweden: "As used in Sweden (Julian to 17 Feb 1753)",
    bulgaria: "As used in Bulgaria (Julian to 31 Mar 1916)",
    russia: "As used in Russia (Julian to 31 Jan 1918)",
    romania: "As used in Romania (Julian to 31 Mar 1919)",
    greece: "As used in Greece (Julian to 15 Feb 1923)",
  },
  calendarHint:
    "A date in an old document is in the calendar its country used then. Pick that place to read it correctly.",
  today: "Today",
  dateFormat: "{day} {month} {year}",
  calendarUsed: "Calendar used",
  usedGregorian: "Gregorian",
  usedJulian: "Julian",
  isoWeekday: "ISO weekday number",
  dayOfYearLabel: "Day of the year",
  leapLabel: "Leap year",
  yes: "Yes",
  no: "No",
  otherDate: "The same day is {date} in the {calendar} calendar.",
  agree:
    "The day count, Zeller's congruence and the Doomsday rule all give {weekday}.",
  zellerHeading: "Zeller's congruence",
  doomsdayHeading: "Doomsday rule",
  stepColumn: "Step",
  workingColumn: "Working",
  resultColumn: "Result",
  zQ: "Day of the month (q)",
  zM: "Month, with January and February counted as 13 and 14 of the year before (m)",
  zMWorking: "{month} → {m}; year {year} → {adjusted}",
  zKJ: "Year of the century (K) and century (J)",
  zKJWorking: "{adjusted} → K = {k}, J = {j}",
  zMonthTerm: "Month term ⌊13(m + 1) / 5⌋",
  zMonthWorking: "⌊13 × {mp1} / 5⌋",
  zLeapTerm: "Leap-year term ⌊K / 4⌋",
  zLeapWorking: "⌊{k} / 4⌋",
  zCenturyGregorian: "Century term ⌊J / 4⌋ + 5J",
  zCenturyGregorianWorking: "⌊{j} / 4⌋ + 5 × {j}",
  zCenturyJulian: "Century term 5 − J",
  zCenturyJulianWorking: "5 − {j}",
  zSum: "Sum, then remainder after dividing by 7 (h)",
  zSumWorking: "{sum} mod 7",
  zWeekday: "h = 0 Saturday, 1 Sunday, 2 Monday … 6 Friday",
  dCentury: "Anchor day of the century",
  dCenturyGregorian: "(5 × ({century} mod 4) + 2) mod 7",
  dCenturyJulian: "(−{century}) mod 7",
  dYear: "Years since the century: ⌊y / 12⌋ + (y mod 12) + ⌊(y mod 12) / 4⌋",
  dYearWorking: "y = {yy}: {a} + {b} + {c} = {sum}, mod 7 = {shift}",
  dDoomsday: "The year's doomsday: anchor + shift",
  dDoomsdayWorking: "{anchor} + {shift} days",
  dMonth: "Doomsday date in the month",
  dMonthWorking: "{month} {date}",
  dOffset: "Days from that date to the day asked",
  dOffsetWorking: "({day} − {date}) mod 7",
  dResult: "Doomsday + offset",
  dResultWorking: "{doom} + {offset} days",
  pickDate: "Enter a year and a day.",
  invalidNumbers: "Enter the year and day as whole numbers.",
  invalidRange: "Enter a year from 1 to 9999.",
  nonexistent:
    "That date does not exist in this calendar. Check the month length and whether the year is a leap year.",
  skipped:
    "That date never existed there: the calendar switch dropped it. Choose the day before or after.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{date} is a {weekday}",
} as const;

type Strings = typeof DAY_OF_WEEK_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

interface Fields {
  year: string;
  month: string;
  day: string;
}

type Reading =
  | { date: CivilDate; kind: CalendarKind; rd: number }
  | { error: string }
  | null;

function todayParts(): Fields {
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1),
    day: String(now.getDate()),
  };
}

function read(
  fields: Fields,
  option: "gregorian" | "julian" | AdoptionRegion,
  strings: Strings
): Reading {
  if (fields.year.trim() === "" || fields.day.trim() === "") {
    return null;
  }
  const year = parseIntegerInput(fields.year);
  const day = parseIntegerInput(fields.day);
  if (year === null || day === null) {
    return { error: strings.invalidNumbers };
  }
  const date = { year, month: Number(fields.month), day };
  const resolved = resolveDate(option, date);
  if ("error" in resolved) {
    return {
      error: {
        range: strings.invalidRange,
        nonexistent: strings.nonexistent,
        skipped: strings.skipped,
      }[resolved.error],
    };
  }
  return { date, ...resolved };
}

function useNames(strings: Strings) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const weekday = (index: number) =>
    formatDateTime(locale, new Date(Date.UTC(2001, 0, 7 + index)), {
      weekday: "long",
      timeZone: "UTC",
    });
  const month = (index: number) =>
    formatDateTime(locale, new Date(Date.UTC(2001, index - 1, 1)), {
      month: "long",
      timeZone: "UTC",
    });
  const date = (value: CivilDate) =>
    t(strings.dateFormat, {
      day: fmt(value.day, { useGrouping: false }),
      month: month(value.month),
      year: fmt(value.year, { useGrouping: false }),
    });
  return { weekday, month, date };
}

type Names = ReturnType<typeof useNames>;

function zellerRows(
  kind: CalendarKind,
  date: CivilDate,
  strings: Strings,
  names: Names,
  fmt: Fmt
): string[][] {
  const z = zeller(kind, date.year, date.month, date.day);
  const n = (value: number) => fmt(value, { useGrouping: false });
  const gregorian = kind === "gregorian";
  return [
    [strings.zQ, names.date(date), n(z.q)],
    [
      strings.zM,
      t(strings.zMWorking, {
        month: n(date.month),
        m: n(z.m),
        year: n(date.year),
        adjusted: n(z.adjustedYear),
      }),
      n(z.m),
    ],
    [
      strings.zKJ,
      t(strings.zKJWorking, {
        adjusted: n(z.adjustedYear),
        k: n(z.k),
        j: n(z.j),
      }),
      `${n(z.k)}, ${n(z.j)}`,
    ],
    [
      strings.zMonthTerm,
      t(strings.zMonthWorking, { mp1: n(z.m + 1) }),
      n(z.monthTerm),
    ],
    [strings.zLeapTerm, t(strings.zLeapWorking, { k: n(z.k) }), n(z.leapTerm)],
    [
      gregorian ? strings.zCenturyGregorian : strings.zCenturyJulian,
      t(
        gregorian
          ? strings.zCenturyGregorianWorking
          : strings.zCenturyJulianWorking,
        { j: n(z.j) }
      ),
      n(z.centuryTerm),
    ],
    [
      strings.zSum,
      t(strings.zSumWorking, { sum: n(z.sum) }),
      `h = ${n(z.h)} (${names.weekday(z.weekday)})`,
    ],
  ];
}

function doomsdayRows(
  kind: CalendarKind,
  date: CivilDate,
  strings: Strings,
  names: Names,
  fmt: Fmt
): string[][] {
  const d = doomsday(kind, date.year, date.month, date.day);
  const n = (value: number) => fmt(value, { useGrouping: false });
  return [
    [
      strings.dCentury,
      t(
        kind === "gregorian"
          ? strings.dCenturyGregorian
          : strings.dCenturyJulian,
        { century: n(d.century) }
      ),
      names.weekday(d.anchor),
    ],
    [
      strings.dYear,
      t(strings.dYearWorking, {
        yy: n(d.yy),
        a: n(d.twelves),
        b: n(d.remainder),
        c: n(d.fours),
        sum: n(d.twelves + d.remainder + d.fours),
        shift: n(d.yearShift),
      }),
      n(d.yearShift),
    ],
    [
      strings.dDoomsday,
      t(strings.dDoomsdayWorking, {
        anchor: names.weekday(d.anchor),
        shift: n(d.yearShift),
      }),
      names.weekday(d.doomsday),
    ],
    [
      strings.dMonth,
      t(strings.dMonthWorking, {
        month: names.month(date.month),
        date: n(d.doomsdayInMonth),
      }),
      n(d.doomsdayInMonth),
    ],
    [
      strings.dOffset,
      t(strings.dOffsetWorking, {
        day: n(date.day),
        date: n(d.doomsdayInMonth),
      }),
      n(d.offset),
    ],
    [
      strings.dResult,
      t(strings.dResultWorking, {
        doom: names.weekday(d.doomsday),
        offset: n(d.offset),
      }),
      names.weekday(d.weekday),
    ],
  ];
}

function ResultPanel({
  reading,
  strings,
}: {
  reading: { date: CivilDate; kind: CalendarKind; rd: number };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const names = useNames(strings);
  const { date, kind, rd } = reading;
  const weekday = weekdayFromRd(rd);
  const dateText = names.date(date);
  const otherKind = kind === "gregorian" ? "julian" : "gregorian";
  const other =
    otherKind === "gregorian" ? gregorianFromRd(rd) : julianFromFixed(rd);
  const head = [
    strings.stepColumn,
    strings.workingColumn,
    strings.resultColumn,
  ];
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {dateText}: {names.weekday(weekday)}
        </h2>
        <StatGrid>
          <StatCard emphasis label={dateText} value={names.weekday(weekday)} />
          <StatCard
            label={strings.isoWeekday}
            value={fmt(weekday === 0 ? 7 : weekday)}
          />
          <StatCard
            label={strings.dayOfYearLabel}
            value={fmt(dayOfYear(kind, date.year, date.month, date.day))}
          />
          <StatCard
            label={strings.leapLabel}
            value={isLeapYearIn(kind, date.year) ? strings.yes : strings.no}
          />
          <StatCard
            label={strings.calendarUsed}
            value={
              kind === "gregorian" ? strings.usedGregorian : strings.usedJulian
            }
          />
        </StatGrid>
        <p className="text-foreground text-sm">
          {t(strings.otherDate, {
            date: names.date(other),
            calendar:
              otherKind === "gregorian"
                ? strings.usedGregorian
                : strings.usedJulian,
          })}{" "}
          {t(strings.agree, { weekday: names.weekday(weekday) })}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            date: dateText,
            weekday: names.weekday(weekday),
          })}
        />
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.zellerHeading}</h2>
        <DataTable
          head={head}
          rows={zellerRows(kind, date, strings, names, fmt)}
        />
        <p className="text-muted-foreground text-sm">{strings.zWeekday}</p>
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.doomsdayHeading}</h2>
        <DataTable
          head={head}
          rows={doomsdayRows(kind, date, strings, names, fmt)}
        />
      </section>
    </>
  );
}

export default function DayOfWeekCalculatorWidget() {
  const strings = useToolUi(DAY_OF_WEEK_CALCULATOR_STRINGS);
  const names = useNames(strings);
  const [fields, setFields] = useState<Fields>({
    year: "",
    month: "1",
    day: "",
  });
  const [calendar, setCalendar] = useState<string>("gregorian");

  useEffect(() => {
    setFields((current) => (current.year === "" ? todayParts() : current));
  }, []);

  const setField = (key: keyof Fields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));
  const region = ADOPTION_REGIONS.find((entry) => entry.id === calendar);
  const reading = read(
    fields,
    region ?? (calendar === "julian" ? "julian" : "gregorian"),
    strings
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr]">
        <TextField
          id="dw-year"
          label={strings.year}
          numeric
          onChange={setField("year")}
          value={fields.year}
        />
        <SelectField
          id="dw-month"
          label={strings.month}
          onChange={setField("month")}
          options={MONTHS.map((month) => ({
            value: String(month),
            label: names.month(month),
          }))}
          value={fields.month}
        />
        <TextField
          id="dw-day"
          label={strings.day}
          numeric
          onChange={setField("day")}
          value={fields.day}
        />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <SelectField
            hint={strings.calendarHint}
            id="dw-calendar"
            label={strings.calendar}
            onChange={setCalendar}
            options={[
              { value: "gregorian", label: strings.calGregorian },
              { value: "julian", label: strings.calJulian },
              ...ADOPTION_REGIONS.map((entry) => ({
                value: entry.id,
                label: strings.regions[entry.id as keyof Strings["regions"]],
              })),
            ]}
            value={calendar}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields(todayParts());
            setCalendar("gregorian");
          }}
          type="button"
        >
          {strings.today}
        </button>
      </div>
      {reading === null ? <Notice>{strings.pickDate}</Notice> : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {reading !== null && "rd" in reading ? (
        <ResultPanel reading={reading} strings={strings} />
      ) : null}
    </div>
  );
}
